/**
 * 「声音与字幕」微调 —— 两态收敛批(2026-07-26)从 8 组控件砍到 4 组。
 *
 * ═══ 砍掉了什么、为什么(设计稿 §9.1,判据 = 用户可感知度 × 引擎是否已有更好的自动值)═══
 * - 情感 chips(删):只对 3 个音色生效,且引擎已**逐镜**产 `voiceEmotions`(按每镜口播内容
 *   配情绪)—— 全局控件反而更粗。字段保留走 AI 值。
 * - 音量 slider(删):与 BGM 的相对响度,默认 75 没人动。
 * - 字幕字体(删):AI 按风格建议(`suggestedFontStyle`),跟风格走。
 * - 字幕位置(删,固定底部):竖屏营销片顶/中会挡 hook 画面;大字卡是导演层 ASS 的事。
 * - 转场(删):AI 按拍法定(hard 为主)。
 * - 语速 slider → **3 档 chips**:连续滑杆是伪精度;AI 按拍法建议的值落在哪档亮哪档。
 * 留下的 = 音色(第一控件)/ 语速 3 档 / 字幕大小(用户原话:「字幕其实就是选大小」)/ BGM。
 * 被删字段全部保留在 FormState 走 AI/默认值,契约零改。
 */
import { ComplianceAudioBadge } from "@/contexts/ComplianceAudioBadge";
import { useState, type ReactNode } from "react";
import { AudioLines, ChevronDown } from "lucide-react";
import { useQuery } from "@tanstack/react-query";

import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/shared/ui/collapsible";
import { OptionChip, OptionChipGroup } from "./OptionChip";
import { SUBTITLE_SIZES, VOICE_OPTIONS } from "@/shared/lib/video/labels";
import { cn } from "@/shared/lib/utils";
import { pauseOtherAudio } from "@/shared/lib/exclusiveAudio";
import { dhProfileService } from "@/entities/dh-profile/api/dhProfileService";

interface VoiceSubtitleValues {
  voiceTone?: string | null;
  voiceSpeechRate?: number | null;
  subtitleFontScale?: string | null;
}

interface Props {
  value: VoiceSubtitleValues;
  onChange: (patch: VoiceSubtitleValues) => void;
  /** 背景音乐面板(收进本可折叠面板内,与配音/字幕同处一个微调入口) */
  bgmSlot?: ReactNode;
}

/** 语速三档:AI 建议值(连续)就近落档显示;点档写档位代表值。 */
const RATE_STEPS = [
  { value: 1.0, label: "舒缓 1.0" },
  { value: 1.1, label: "标准 1.1" },
  { value: 1.22, label: "带货快 1.22" },
] as const;

// 仅本文件内用(导出会触发 react-refresh/only-export-components:文件同时导出组件与函数)
function nearestRateStep(rate: number | null | undefined): number {
  const r = rate ?? 1.1;
  let best: number = RATE_STEPS[0].value;
  for (const s of RATE_STEPS) {
    if (Math.abs(s.value - r) < Math.abs(best - r)) best = s.value;
  }
  return best;
}

/**
 * ★私有 Chip 已删(2026-08-06 重排):它是同一串 chip 样式在本仓的**第四份拷贝**,
 * 且与另外三份一样**没有 focus 环** —— 键盘用户看不见自己停在哪。
 * 统一走 `OptionChip`;音色/语速/字幕都是单选组,语义用 radio 而不是 aria-pressed。
 */

/**
 * 音色试听样音(2026-08-14 四天反馈校对 TOP#13)——「配音不可以试听,只能盲选」。
 *
 * ★这条从 0811 起玉玉/莉莉/爱民/雷四人四次提过,一直被当成「已随 0813 音色互斥修掉」。
 *   数一遍就知道没有:那批修的是**已经有试听的那些页**(分身舱/BGM/声音库共 9 处挂
 *   `onPlay={pauseOtherAudio}`),而本文件里 `<audio>` 实数是 **0** —— 视频舱这 12 个音色
 *   从来只是 chip,没有播放器可点。判成「已修」只会让它继续留在盲区里。
 *
 * 样音复用分身舱既有资产(`PresetVoiceCatalog` seed 进 MEDIA 桶 `dh/preset/voice/*.mp3`,
 * 各音色念同一句 SAMPLE_TEXT —— 同文案才能横向比音色)。两边音色 id 是同一批 `long*`,
 * 12 款里 8 款直接对得上;另 4 款(清甜推销/活泼质感/经典直播/博才干练)后端没有样音,
 * **如实说没有,不做假试听** —— 这是 PresetVoiceCatalog 自己立的规矩,照它执行。
 *
 * 拿不到样音(未登录/接口挂/未 seed)不弹错、不塌:面板照常能选,只是不显试听条。
 */
function usePresetVoiceSamples(): Map<string, string> {
  const q = useQuery({
    queryKey: ["dh", "preset-voices"], // 与分身舱同 key,同页切换命中缓存不重复请求
    queryFn: () => dhProfileService.listPresetVoices(),
    staleTime: 5 * 60_000,
    retry: false,
    // 有回落(拿不到就不显试听条,选择器照样能用)→ 再弹「加载失败」是在为没坏的事报警。
    meta: { suppressGlobalErrorToast: true },
  });
  const m = new Map<string, string>();
  for (const v of q.data ?? []) {
    if (v.sampleUrl) m.set(v.id, v.sampleUrl);
  }
  return m;
}

export function VoiceSubtitlePanel({ value, onChange, bgmSlot }: Props) {
  const [open, setOpen] = useState(false);
  const samples = usePresetVoiceSamples();
  const tone = value.voiceTone || "longanhuan";
  const rateStep = nearestRateStep(value.voiceSpeechRate);
  const size = value.subtitleFontScale || "medium";
  const summary = [
    VOICE_OPTIONS.find((v) => v.value === tone)?.label,
    RATE_STEPS.find((s) => s.value === rateStep)?.label.split(" ")[0],
    `字幕${SUBTITLE_SIZES.find((s) => s.value === size)?.label}号`,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    /* ★折叠实现统一走 Radix Collapsible(2026-08-06 重排):此前本页三套折叠并存 ——
       Radix Collapsible(高级选项/故事大纲)、原生 `<details>`(这里)、Dialog(风格画廊)。
       原生 details 的开合态只能用 `group-open:` 这类 CSS hack 表达,与其余折叠件
       动画/键盘行为都不一致。图标同批从 emoji 换成 lucide(🎚 不是图标,是字)。 */
    <Collapsible open={open} onOpenChange={setOpen} className="rounded-lg border bg-muted/20">
      <CollapsibleTrigger asChild>
        <button
          type="button"
          className="flex w-full cursor-pointer items-center justify-between px-4 py-3 text-left"
        >
          <span className="flex items-center gap-1.5 text-sm font-medium">
            <AudioLines className="h-4 w-4 text-muted-foreground" strokeWidth={1.8} />
            声音与字幕
          </span>
          <span className="flex items-center gap-2 text-xs text-muted-foreground">
            <span className="hidden max-w-[60%] truncate sm:inline">
              {value.voiceTone ? `AI 已设:${summary}` : summary}
            </span>
            <span className="text-primary">{open ? "收起" : "点开微调"}</span>
            <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", open && "rotate-180")} />
          </span>
        </button>
      </CollapsibleTrigger>

      <CollapsibleContent className="space-y-4 border-t px-4 py-4">
        <div className="space-y-1.5">
          <span className="text-xs text-muted-foreground">配音音色(AI 按目的选好了,可换)</span>
          <OptionChipGroup label="配音音色">
            {VOICE_OPTIONS.map((v) => (
              <OptionChip
                key={v.value}
                radio
                active={tone === v.value}
                tabIndex={tone === v.value ? 0 : -1}
                onClick={() => onChange({ voiceTone: v.value })}
              >
                {v.label}
              </OptionChip>
            ))}
          </OptionChipGroup>
          {/* ★试听条挂在 radiogroup **外面**:组里塞一颗非 radio 的播放按钮会破坏
              「Tab 进组一次、方向键在组内选」那套语义(组的方向键处理器只认 role=radio,
              多出来的按钮对读屏就是组里一个说不清是什么的东西)。
              放外面还顺带解决了「先比再定」:选中在这一步零成本(积分要到出片才花),
              点一颗听一颗就是横向对比 —— 不必为了试听先做一次假选择。 */}
          {samples.has(tone) ? (
            <div className="flex items-center gap-2 pt-0.5" data-testid="voice-sample-player">
              <span className="shrink-0 text-2xs text-muted-foreground">
                试听「{VOICE_OPTIONS.find((v) => v.value === tone)?.label}」
              </span>
              <audio
                key={tone}
                src={samples.get(tone)}
                controls
                preload="none"
                onPlay={pauseOtherAudio}
                className="h-7 max-w-[240px] flex-1"
              >
                <track kind="captions" />
              </audio>
              <ComplianceAudioBadge />
            </div>
          ) : (
            <p className="pt-0.5 text-2xs text-muted-foreground" data-testid="voice-sample-none">
              这款暂无样音,换一款可试听
            </p>
          )}
          <p className="text-2xs text-muted-foreground">逐镜情绪由 AI 按口播内容配,不用统一定</p>
        </div>

        <div className="space-y-1.5">
          <span className="text-xs text-muted-foreground">语速</span>
          <OptionChipGroup label="语速">
            {RATE_STEPS.map((s) => (
              <OptionChip
                key={s.value}
                radio
                active={rateStep === s.value}
                tabIndex={rateStep === s.value ? 0 : -1}
                onClick={() => onChange({ voiceSpeechRate: s.value })}
              >
                {s.label}
              </OptionChip>
            ))}
          </OptionChipGroup>
        </div>

        <div className="space-y-1.5">
          <span className="text-xs text-muted-foreground">字幕</span>
          <OptionChipGroup label="字幕大小">
            {SUBTITLE_SIZES.map((s) => (
              <OptionChip
                key={s.value}
                radio
                active={size === s.value}
                tabIndex={size === s.value ? 0 : -1}
                onClick={() => onChange({ subtitleFontScale: s.value })}
              >
                {s.label}
              </OptionChip>
            ))}
          </OptionChipGroup>
          <p className="text-2xs text-muted-foreground">
            口播为空则不烧字幕;字体随风格、固定底部,不用管
          </p>
        </div>

        {bgmSlot && (
          <div className="space-y-1.5">
            <span className="text-xs text-muted-foreground">背景音乐</span>
            {bgmSlot}
          </div>
        )}
      </CollapsibleContent>
    </Collapsible>
  );
}
